import React, { useState, useEffect, useRef } from 'react';
import { Check, X, FolderOpen } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const InlineCollectionCreator = ({ 
  projectId, 
  userInfo, 
  viewMode = "grid", 
  onCancel, 
  onSuccess,
  onError 
}) => {
  const [collectionName, setCollectionName] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleCreate = async () => {
    if (!collectionName.trim()) {
      onError?.('Please enter a collection name');
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/collections/projects/${projectId}/collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: collectionName.trim(),
          userId: userInfo.userId
        })
      });
      
      const responseText = await response.text();
      console.log('Collection response status:', response.status);
      console.log('Collection response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse collection JSON response:', parseError);
        console.error('Raw response text:', responseText);
        throw new Error(`Server returned invalid JSON for collection: ${responseText.substring(0, 200)}...`);
      }
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create collection');
      }
      
      onSuccess?.(data);
    } catch (err) {
      console.error('Error creating collection:', err);
      onError?.(err.message || 'Failed to create collection');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCreate();
    } else if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  if (viewMode === "list") {
    return (
      <Card className="border-slate-700 bg-slate-900 border-dashed border-2 border-blue-500/50">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-6 ">
            {/* Left section - Icon and Input */}
            <div className="flex items-center gap-4 flex-1 mt-4 min-w-0">
              {/* Icon */}
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-500/50">
                <FolderOpen className="h-5 w-5 text-white" />
              </div>
              
              {/* Input */}
              <input
                ref={inputRef}
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Enter collection name..."
                className="flex-1 bg-transparent text-slate-100 text-base font-semibold placeholder-slate-500 border-none outline-none focus:ring-0"
                disabled={loading}
              />
            </div>
            
            {/* Right section - Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleCreate}
                disabled={loading || !collectionName.trim()}
                className="p-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
                title="Create collection"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Check size={16} />
                )}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="p-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white transition-colors"
                title="Cancel"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Grid view
  return (
    <Card className="h-fit border-slate-700 bg-slate-900 border-dashed border-2 border-blue-500/50">
      <CardContent className="p-4 space-y-3">
        {/* Icon and Input */}
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Collection name..."
            className="flex-1 bg-transparent text-slate-100 text-sm font-semibold placeholder-slate-500 border-none outline-none focus:ring-0"
            disabled={loading}
          />
        </div>

        {/* Placeholder for tags section */}
        <div className="min-h-[20px]"></div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-700/50">
          <button
            onClick={onCancel}
            disabled={loading}
            className="p-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg text-white transition-colors"
            title="Cancel"
          >
            <X size={12} />
          </button>
          <button
            onClick={handleCreate}
            disabled={loading || !collectionName.trim()}
            className="p-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
            title="Create collection"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
            ) : (
              <Check size={12} />
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InlineCollectionCreator;